import axios from 'axios';
import { httpClient } from '@/api/httpClient';
import {
  mapPaginatedResource,
  type PaginatedApiResource,
  type PaginatedResult,
} from '@/api/pagination.types';
import {
  mapApiResourceToIntakeSubmission,
  mapApiResourceToIntakeSubmissionListItem,
  mapApiResourceToOwnerIntakeSubmission,
  mapApiResourceToWorkspaceIntakeSubmissionListItem,
  mapIntakeReturnPayload,
  mapOwnerIntakeDraftPayload,
  type IntakeSubmission,
  type IntakeSubmissionApiResource,
  type IntakeSubmissionListItem,
  type IntakeSubmissionListItemApiResource,
  type OwnerIntakeDraftPayload,
  type OwnerIntakeSubmission,
  type OwnerIntakeSubmissionApiResource,
  type WorkspaceIntakeSubmissionListItem,
  type WorkspaceIntakeSubmissionListItemApiResource,
} from './intakeApi.types';

export async function fetchOwnIntakeSubmissions(): Promise<IntakeSubmissionListItem[]> {
  const response =
    await httpClient.get<IntakeSubmissionListItemApiResource[]>('/me/intake/submissions');
  return response.data.map(mapApiResourceToIntakeSubmissionListItem);
}

export async function fetchOwnIntakeSubmission(
  submissionId: string
): Promise<OwnerIntakeSubmission> {
  const response = await httpClient.get<OwnerIntakeSubmissionApiResource>(
    '/me/intake/submissions/' + submissionId
  );
  return mapApiResourceToOwnerIntakeSubmission(response.data);
}

export async function fetchWorkspaceIntakeSubmissions(
  page: number,
  pageSize: number,
  search: string,
  status: string
): Promise<PaginatedResult<WorkspaceIntakeSubmissionListItem>> {
  const trimmedSearch = search.trim();
  const response = await httpClient.get<
    PaginatedApiResource<WorkspaceIntakeSubmissionListItemApiResource>
  >('/intake-submissions', {
    params: {
      page,
      page_size: pageSize,
      search: trimmedSearch.length === 0 ? undefined : trimmedSearch,
      status: status.length === 0 ? undefined : status,
    },
  });
  return mapPaginatedResource(response.data, mapApiResourceToWorkspaceIntakeSubmissionListItem);
}

export async function fetchWorkspaceIntakeSubmission(
  submissionId: string
): Promise<IntakeSubmission> {
  const response = await httpClient.get<IntakeSubmissionApiResource>(
    '/intake-submissions/' + submissionId
  );
  return mapApiResourceToIntakeSubmission(response.data);
}

export async function saveOwnIntakeDraft(
  submissionId: string | null,
  payload: OwnerIntakeDraftPayload
): Promise<OwnerIntakeSubmission> {
  const body = mapOwnerIntakeDraftPayload(payload);
  if (submissionId === null) {
    const response = await httpClient.post<OwnerIntakeSubmissionApiResource>('/me/intake', body);
    return mapApiResourceToOwnerIntakeSubmission(response.data);
  }
  const response = await httpClient.patch<OwnerIntakeSubmissionApiResource>(
    '/me/intake/' + submissionId,
    body
  );
  return mapApiResourceToOwnerIntakeSubmission(response.data);
}

export async function submitOwnIntake(submissionId: string): Promise<OwnerIntakeSubmission> {
  const response = await httpClient.post<OwnerIntakeSubmissionApiResource>(
    '/me/intake/' + submissionId + '/submit'
  );
  return mapApiResourceToOwnerIntakeSubmission(response.data);
}

export async function discardOwnIntakeDraft(submissionId: string): Promise<void> {
  await httpClient.delete('/me/intake/' + submissionId);
}

export async function fetchPatientIntakeSubmission(
  patientId: string
): Promise<IntakeSubmission | null> {
  try {
    const response = await httpClient.get<IntakeSubmissionApiResource>(
      '/patients/' + patientId + '/intake-submission'
    );
    return mapApiResourceToIntakeSubmission(response.data);
  } catch (fetchError) {
    if (
      axios.isAxiosError(fetchError) &&
      fetchError.response !== undefined &&
      fetchError.response.status === 404
    ) {
      return null;
    }
    throw fetchError;
  }
}

export async function acceptIntakeSubmission(submissionId: string): Promise<IntakeSubmission> {
  const response = await httpClient.post<IntakeSubmissionApiResource>(
    '/intake-submissions/' + submissionId + '/accept'
  );
  return mapApiResourceToIntakeSubmission(response.data);
}

export async function returnIntakeSubmission(
  submissionId: string,
  reviewNote: string
): Promise<IntakeSubmission> {
  const response = await httpClient.post<IntakeSubmissionApiResource>(
    '/intake-submissions/' + submissionId + '/return',
    mapIntakeReturnPayload(reviewNote)
  );
  return mapApiResourceToIntakeSubmission(response.data);
}
